/**
 * §11 — the vocabulary of the kids desk: capacity, the queue, the wristband, the
 * child and the adults who may collect them. It lives beside the dialogs rather
 * than in `../lib` because no other surface on the floor speaks it; a PS5 is an
 * ordinary timed resource.
 *
 * ## What changed under this file
 *
 * A child used to be a `PartyParticipant` — a row that lived for one party and
 * died with it — and the guardian was that participant's generic
 * `hostParticipantId` link. Both are gone. A child is now a persistent `Child`
 * record with its own `Guardian` rows behind it, which buys the desk two things
 * it did not have:
 *
 *  - **A returning child is recognised, not retyped.** `GET /children` finds one
 *    by band, by name, or by a parent's phone number.
 *  - **A child may have more than one guardian**, so the second parent no longer
 *    needs a manager's signature to collect their own child.
 *
 * And it costs one thing, deliberately: `GET /resource-sessions` no longer
 * carries the child at all. A child's name, band and guardians' phone numbers
 * sit behind `children.view`, which the floor roles do not hold, so the session
 * payload carries `childId` and the desk — which does hold the key — reads the
 * rest itself. That is why {@link useChildDirectory} exists.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { SessionRow } from '../lib/sessions';

/** `GET /kids-area/occupancy/:resourceId`. */
export interface KidsOccupancy {
  resourceId: string;
  resourceName: string;
  resourceType: string;
  capacity: number;
  active: number;
  paused: number;
  occupied: number;
  free: number;
  waitingCount: number;
  calledCount: number;
}

export type WaitingListStatus = 'WAITING' | 'CALLED' | 'SEATED' | 'CANCELLED';

/** A ticket as `GET /kids-area/waiting-list` folds it out of the ledger. */
export interface WaitingListTicket {
  ticketId: string;
  resourceId: string;
  status: WaitingListStatus;
  /** 1-based place in line; null once the ticket is no longer waiting. */
  position: number | null;
  childId: string | null;
  childName: string | null;
  guardianId: string | null;
  guardianName: string | null;
  tagCode: string | null;
  /** How many children this ticket covers — a head count, not a `Party`. */
  partySize: number;
  note: string | null;
  joinedAt: string;
  calledAt: string | null;
  seatedAt: string | null;
  cancelledAt: string | null;
  sessionId: string | null;
}

/** `POST /kids-area/check-in` — a seat, or a place in line when the area is full. */
export interface KidsCheckInResult {
  checkedIn: boolean;
  session: SessionRow | null;
  waitingListTicket: WaitingListTicket | null;
}

// ------------------------------------------------------- children & guardians

/** An adult who may collect a child. One row per phone number, per tenant. */
export interface GuardianRow {
  id: string;
  name: string;
  phone: string;
  backupPhone: string | null;
  customerId: string | null;
}

/** The link between the two, which is where the call order lives. */
export interface ChildGuardianLink {
  guardianId: string;
  relationship: string | null;
  isPrimary: boolean;
  guardian: GuardianRow;
}

/** `GET /children/:id` — a name, a band, and who to call. Nothing else. */
export interface ChildRow {
  id: string;
  displayName: string;
  tagCode: string | null;
  guardians: ChildGuardianLink[];
}

/** One guardian on a form. Mirrors `GuardianInputDto` field for field. */
export interface GuardianDraft {
  name: string;
  phone: string;
  backupPhone: string;
  relationship: string;
  isPrimary: boolean;
}

export const emptyGuardian: GuardianDraft = {
  name: '',
  phone: '',
  backupPhone: '',
  relationship: '',
  isPrimary: false,
};

/** `GuardianInputDto` requires a name and a phone; the rest are omitted if blank. */
export function guardianIsComplete(draft: GuardianDraft): boolean {
  return draft.name.trim().length > 0 && draft.phone.trim().length >= 4;
}

/**
 * A draft as the API takes it.
 *
 * `forbidNonWhitelisted` means a blank string is not the same as an absent
 * field — `backupPhone: ''` would be written as an empty number the desk would
 * later try to ring — so empties are dropped rather than sent.
 */
export function guardianBody(draft: GuardianDraft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    phone: draft.phone.trim(),
    ...(draft.backupPhone.trim() ? { backupPhone: draft.backupPhone.trim() } : {}),
    ...(draft.relationship.trim() ? { relationship: draft.relationship.trim() } : {}),
    ...(draft.isPrimary ? { isPrimary: true } : {}),
  };
}

/** The guardians on a child's record, the one to try first at the front. */
export function guardiansOf(child: ChildRow | null): ChildGuardianLink[] {
  return child?.guardians ?? [];
}

/** Ids only — what {@link collectionState} compares the desk's answer against. */
export function guardianIdsOf(child: ChildRow | null): string[] {
  return guardiansOf(child).map((link) => link.guardianId);
}

/**
 * The one guardian to preselect at check-out, or nothing.
 *
 * Nothing when the child has two, and that is the server's rule rather than a
 * simplification: with more than one on file there is no unambiguous default,
 * and "a guardian collected them" is not a usable answer to "who has my child".
 */
export function unambiguousGuardianId(child: ChildRow | null): string {
  const links = guardiansOf(child);
  return links.length === 1 ? links[0].guardianId : '';
}

/** `GET /children` — by band, by name, or by a parent's number. */
export async function searchChildren(query: {
  name?: string;
  tagCode?: string;
  guardianPhone?: string;
}): Promise<ChildRow[]> {
  const params: Record<string, string> = { limit: '20' };
  if (query.name?.trim()) params.name = query.name.trim();
  if (query.tagCode?.trim()) params.tagCode = query.tagCode.trim();
  if (query.guardianPhone?.trim()) params.guardianPhone = query.guardianPhone.trim();
  const { data } = await api.get<{ data?: ChildRow[] }>('/children', { params });
  return data?.data ?? [];
}

/** `POST /children` — a child the venue did not know, and at least one guardian. */
export async function createChild(input: {
  displayName: string;
  tagCode?: string;
  guardians: GuardianDraft[];
}): Promise<ChildRow> {
  const { data } = await api.post<ChildRow>('/children', {
    displayName: input.displayName.trim(),
    ...(input.tagCode?.trim() ? { tagCode: input.tagCode.trim() } : {}),
    guardians: input.guardians.map(guardianBody),
  });
  return data;
}

/** `POST /children/:id/guardians` — a second parent, or a grandmother. */
export async function attachGuardian(childId: string, draft: GuardianDraft): Promise<ChildRow> {
  const { data } = await api.post<ChildRow>(`/children/${childId}/guardians`, {
    guardian: guardianBody(draft),
  });
  return data;
}

/**
 * `DELETE /children/:id/guardians/:guardianId`.
 *
 * The server refuses the last one — a child with nobody to call is the state the
 * record exists to prevent — so the desk's job is to add the replacement first.
 */
export async function detachGuardian(childId: string, guardianId: string): Promise<ChildRow> {
  const { data } = await api.delete<ChildRow>(`/children/${childId}/guardians/${guardianId}`);
  return data;
}

export function useKidsOccupancy(resourceId: string | null, enabled: boolean, revision = 0) {
  const [occupancy, setOccupancy] = useState<KidsOccupancy | null>(null);

  const load = useCallback(async () => {
    if (!resourceId || !enabled) {
      setOccupancy(null);
      return;
    }
    try {
      const { data } = await api.get<KidsOccupancy>(`/kids-area/occupancy/${resourceId}`);
      setOccupancy(data ?? null);
    } catch {
      // Without `resource_sessions.view` the card falls back to counting the
      // sessions it was already given, rather than showing a broken tile.
      setOccupancy(null);
    }
  }, [resourceId, enabled]);

  useEffect(() => {
    void load();
    // `revision` re-reads the server's count after a check-in or check-out.
  }, [load, revision]);

  return { occupancy, reload: load };
}

export function useWaitingList(resourceId: string | null, enabled: boolean, revision = 0) {
  const [tickets, setTickets] = useState<WaitingListTicket[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!resourceId || !enabled) {
      setTickets([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get<WaitingListTicket[]>('/kids-area/waiting-list', {
        params: { resourceId },
      });
      setTickets(Array.isArray(data) ? data : []);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [resourceId, enabled]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  return { tickets, loading, reload: load };
}

/**
 * The children on the board, by id.
 *
 * `GET /resource-sessions` hands the desk a `childId` and stops there, on
 * purpose: the shape is read by every waiter on the floor map and a child's
 * guardians are not theirs to see. So the desk — which holds `children.view` —
 * asks for each child it is actually showing. One read per child, and a kids
 * area holds a handful; the old code did exactly this one read per party.
 *
 * A refused read (a terminal without the key) leaves the child absent rather
 * than the board broken: the card then says a child is in the room without
 * naming them, which is the honest answer.
 */
export function useChildDirectory(childIds: string[]): Record<string, ChildRow> {
  const [children, setChildren] = useState<Record<string, ChildRow>>({});

  // A stable key, so the effect re-runs when the SET of children changes and
  // not on every repaint of the meter above it.
  const key = useMemo(() => [...new Set(childIds.filter(Boolean))].sort().join(','), [childIds]);

  useEffect(() => {
    if (!key) {
      setChildren({});
      return;
    }
    let cancelled = false;
    void Promise.all(
      key.split(',').map((childId) =>
        api
          .get<ChildRow>(`/children/${childId}`)
          .then(({ data }) => data)
          .catch(() => null),
      ),
    ).then((rows) => {
      if (cancelled) return;
      const map: Record<string, ChildRow> = {};
      for (const row of rows) if (row?.id) map[row.id] = row;
      setChildren(map);
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return children;
}
